import { AlertTriangle, Ruler } from 'lucide-react';
import type { SpacingIssue } from '@peles/shared';
import { Popover } from '../common/Popover';

export interface SpacingControlProps {
  issues: SpacingIssue[];
  clearanceFt: number;
  /** Name of the item a spacing key (an item uid) points at. */
  nameOf: (uid: string) => string;
  showOnPlan: boolean;
  onShowOnPlanChange: (value: boolean) => void;
  preventOverlap: boolean;
  onPreventOverlapChange: (value: boolean) => void;
  /** Select the two items of an issue on the canvas. */
  onSelectPair: (a: string, b: string) => void;
}

/** Longest list worth reading in a popover; the canvas shows every one. */
const MAX_LISTED = 50;

const formatFt = (ft: number) => (Number.isInteger(ft) ? `${ft}` : ft.toFixed(1));

/**
 * The toolbar's spacing check: how many places are too close, the switches
 * for drawing them and for refusing a move that overlaps, and the list of
 * them — each a button that selects the pair on the plan.
 */
export function SpacingControl({
  issues,
  clearanceFt,
  nameOf,
  showOnPlan,
  onShowOnPlanChange,
  preventOverlap,
  onPreventOverlapChange,
  onSelectPair,
}: SpacingControlProps) {
  const overlaps = issues.filter((i) => i.kind === 'overlap').length;
  const tight = issues.length - overlaps;
  const tone =
    overlaps > 0
      ? 'bg-red-100 text-red-700'
      : tight > 0
        ? 'bg-amber-100 text-amber-800'
        : showOnPlan
          ? 'bg-blue-100 text-blue-700'
          : 'hover:bg-gray-100';

  return (
    <Popover
      ariaLabel="Spacing"
      panelClassName="w-80"
      trigger={(props) => (
        <button
          {...props}
          type="button"
          className={`flex items-center gap-1 px-2 py-1 rounded ${tone}`}
          data-testid="spacing-toggle"
          title="Tables that overlap, or stand closer than an aisle"
        >
          {issues.length > 0 ? <AlertTriangle size={12} /> : <Ruler size={12} />}
          Spacing
          {issues.length > 0 && (
            <span className="font-semibold" data-testid="spacing-count">
              {issues.length}
            </span>
          )}
        </button>
      )}
    >
      {(close) => (
        <div className="space-y-3 text-sm" data-testid="spacing-panel">
          <div>
            <p className="font-medium text-gray-900">
              {issues.length === 0
                ? `Every table has at least ${formatFt(clearanceFt)} ft around it.`
                : [
                    overlaps > 0 && `${overlaps} overlap${overlaps === 1 ? '' : 's'}`,
                    tight > 0 && `${tight} closer than ${formatFt(clearanceFt)} ft`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              The aisle every table needs is set under Settings → App Settings.
            </p>
          </div>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={showOnPlan}
              onChange={(e) => onShowOnPlanChange(e.target.checked)}
              data-testid="spacing-show"
            />
            Show on the plan
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={preventOverlap}
              onChange={(e) => onPreventOverlapChange(e.target.checked)}
              data-testid="spacing-prevent-overlap"
            />
            Don&apos;t let a move put one thing on another
          </label>

          {issues.length > 0 && (
            <ul className="max-h-64 overflow-y-auto divide-y divide-gray-100 border-t border-gray-100">
              {issues.slice(0, MAX_LISTED).map((issue) => (
                <li key={`${issue.a}|${issue.b}`}>
                  <button
                    type="button"
                    className="w-full text-left py-1.5 flex items-start gap-2 hover:bg-gray-50"
                    onClick={() => {
                      onSelectPair(issue.a, issue.b);
                      close();
                    }}
                    data-testid="spacing-issue"
                  >
                    <span
                      className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${issue.kind === 'overlap' ? 'bg-red-500' : 'bg-amber-500'}`}
                      aria-hidden
                    />
                    <span>
                      {nameOf(issue.a)} and {nameOf(issue.b)}{' '}
                      <span className="text-gray-500">
                        {issue.kind === 'overlap'
                          ? 'overlap'
                          : `are ${formatFt(issue.gapFt)} ft apart`}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
              {issues.length > MAX_LISTED && (
                <li className="py-1.5 text-xs text-gray-500">
                  And {issues.length - MAX_LISTED} more, marked on the plan.
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </Popover>
  );
}
